import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import PaginatedPropertyGrid from './PaginatedPropertyGrid';
import { propertyAPI } from '../utils/api';

/**
 * Homepage section that surfaces the most recently viewed properties for
 * returning visitors. Renders 3 cards per page, with the rest paginated
 * via a 15s-autoplay carousel — same UX as "Propriétés en vedette".
 *
 * Uses slim cached copies from localStorage (no network call). Hidden
 * entirely when the visitor has no history.
 */
const RecentlyViewedSection = () => {
  const { items } = useRecentlyViewed();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const onClear = () => setVersion((v) => v + 1);
    window.addEventListener('realty237:recent-cleared', onClear);
    window.addEventListener('storage', onClear);
    return () => {
      window.removeEventListener('realty237:recent-cleared', onClear);
      window.removeEventListener('storage', onClear);
    };
  }, []);

  const shown = items || [];
  if (shown.length === 0) return null;

  // "+ Voir plus" on phones: after the history, similar listings (same cities/types), then most viewed.
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];
  const fetchSimilar = (skip) => (skip > 0 ? Promise.resolve([]) : propertyAPI.getRecommendations({
    exclude: uniq(shown.map((p) => p._id || p.id)).join(','),
    cities: uniq(shown.map((p) => p.city)).slice(0, 4).join(','),
    types: uniq(shown.map((p) => p.type)).slice(0, 3).join(','),
    limit: 18,
  }).then((r) => (Array.isArray(r.data) ? r.data : [])));

  const handleClearAll = () => {
    try { localStorage.removeItem('realty237:recently_viewed'); } catch { /* ignore */ }
    window.dispatchEvent(new Event('realty237:recent-cleared'));
    setVersion((v) => v + 1);
  };

  return (
    <section className="mb-20" data-testid="recently-viewed-section" key={version}>
      <PaginatedPropertyGrid
        properties={shown}
        loading={false}
        pageSize={3}
        maxItems={9}
        autoplayMs={15000}
        testIdPrefix="recently-viewed"
        mobileLimit={5}
        fetchMore={fetchSimilar}
        renderControls={({ prevButton, nextButton }) => (
          <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 mb-2">
                <Clock className="h-3.5 w-3.5" />
                Récemment consultés
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
                Reprendre où vous en étiez
              </h2>
              <p className="text-stone-500 text-sm sm:text-base mt-2">
                {`Les ${shown.length} dernier${shown.length > 1 ? 's' : ''} bien${shown.length > 1 ? 's' : ''} que vous avez consulté${shown.length > 1 ? 's' : ''} — à un clic pour les revoir.`}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-stone-500 hover:text-stone-700 gap-1.5"
                data-testid="recently-viewed-clear"
              >
                <X className="h-3.5 w-3.5" />
                Effacer
              </Button>
              <Link
                to="/properties"
                className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mr-2"
              >
                Voir toutes les annonces
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <div className="hidden md:flex items-center gap-2">
                {prevButton}
                {nextButton}
              </div>
            </div>
          </div>
        )}
      />
    </section>
  );
};

export default RecentlyViewedSection;
